/**
 * Created by fers on 02.03.16.
 */
import React from 'react';
import {FormattedNumber, FormattedDate, FormattedTime, defineMessages} from 'react-intl';
import {getLocalizedCountries} from '../locales';
import emailAddresses from 'email-addresses';
import {parse, format} from 'libphonenumber-js';
import {expressionBuilder} from "../../common/expression/ExpressionBuilder";
import {VisualBuilder} from "../expression/Expression.react";
import SafeHTML from '../fw/SafeHTML.react';

const countries = getLocalizedCountries();
const messages = defineMessages({
    yes: {
        id: "Tool.yes",
        defaultMessage: "Yes"
    },
    no: {
        id: "Tool.no",
        defaultMessage: "No"
    },
});

/**
 * returns field for column string (eg. parent.children.id}. Returns field for "id"
 *
 * @param column
 * @param fields
 * @returns {*}
 */
export function getMappingForColumn(column, fields) {
    let list = column.split(".");

    let listOfFields = fields;
    let resultItem;

    for (let l of list) {
        resultItem = listOfFields[l];

        if (resultItem) {
            if (resultItem.to) {
                if (resultItem.to && typeof resultItem.to === "function") {
                    listOfFields = resultItem.to().fields();
                } else {
                    listOfFields = resultItem.to.fields();
                }
            }
        } else {
            //throw new Error("Missing item for " + l);
        }
    }

    return resultItem;
}

/**
 * returns fields for column string (eg. parent.children.id}. Returns field array [parent, parent.children, parent.children.id]
 *
 * @param column
 * @param fields
 * @returns {*}
 */
export function getMappingForColumnAsArray(column, fields, alertUndefinedColumns = true) {
    let list = column.split(".");

    let listOfFields = fields;
    let resultItem = [];

    for (let l of list) {
        let item = listOfFields[l];

        if (item) {
            resultItem.push(item);

            if (item.to) {
                if (item.to && typeof item.to === "function") {
                    listOfFields = item.to().fields();
                } else {
                    listOfFields = item.to.fields();
                }
            }
        } else {
			if (alertUndefinedColumns) {
				// eslint-disable-next-line no-console
				console.error("Missing item for " + l);
			}
            return [];
        }
    }

    return resultItem;
}


export function getDataForColumn(column, fields, data) {
    let list = column.split(".");

    let listOfFields = fields;
    let resultItem;
    let result = data;

    for (let l of list) {
        resultItem = listOfFields[l];

        if (result && result[l]) {
            result = result[l]; // nosemgrep

            if (resultItem) {
                if (resultItem.to) {
                    listOfFields = resultItem.to().fields();
                }
            } else {
                throw new Error("Missing item for " + l);
            }
        } else {
            return null;
        }
    }

    return result;
}

export function getDataForColumns(columns, fields, data) {
	let columnsData = columns.map(column => getDataForColumn(column, fields, data));

	let result = {};

	columns.forEach((column, idx) => {
		result[column] = columnsData[idx];
	});

	return result;
}

// Note: for now, the renderFunction is used only for html rendering (nl2br)
// eslint-disable-next-line max-params
export function getFormatted(metadata, field, formatMessage, data, renderFunction) {
    let value = getDataForColumn(field, metadata.fields(), data);

    if (value) {
        return tranformDataForView(metadata.entity(), metadata.fields(field), formatMessage, value, false, 0, renderFunction);
    }

    return null;
}

export function getLabel(metadata, field, formatMessage) {
    let columnMappings = getMappingForColumnAsArray(field, metadata.fields());
    let type = columnMappings[columnMappings.length - 1];
    let parentType = columnMappings[columnMappings.length - 2];

    return formatMessage({defaultMessage: type.name, id: "metadata." + (parentType ? parentType.to().entity() : metadata.entity()) + "." + type.key});
}

// Difference to react-nl2br: the original is creating React.Eleemnt(<br>). But that is not suitable for <span dangerouslySetInnerHTML={text} />
export function nl2plainBr(text) {
	return text.split("\n").join("<br />");
}

function voidRenderFunction(result) {
	return result;
}

// Note: for now, the renderFunction is used only for html rendering (nl2br)
// eslint-disable-next-line max-params
export function tranformDataForView(entityName : string, type, formatMessage, value, isList, idx, renderFunction = voidRenderFunction) {
    if (Array.isArray(value)) {
        /* if value is array, show it as fields separated by comma */
        let handledList = value.map((oneValue, idx) => tranformDataForView(entityName, type, formatMessage, oneValue, isList, idx)).map((oneValue, index, originArray) => {
			if (index === originArray.length - 1) {
				return oneValue;
			}
			return oneValue;
		});

        if (handledList.length <= 1) {
            return handledList;
        }

        let result = [];
        let sep = null;
        for (let item of handledList) {
            if (sep) {
                result.push(sep);
            } else {
                sep = ", ";
            }
            result.push(item);
        }

        return result;
    }

    if (type.type === "enum" || type.type === "enums" || type.type === "country") {
        let values = type.values;
        if (type.type === "country" && !values) {
            values = countries;
        }

        /* ignore enum without values */
        if (!values) {
            return value;
        }

        /* if value is integer, try to use it as ordinary number */
        if (!isNaN(value - 0) && !values[value]) {
            value = Object.keys(values)[value];
        }

        if (values[value]) {
            value = formatMessage({
                id: "metadata." + entityName + "." + type.key + ".values." + value + ".name",
                defaultMessage: values[value]['name']
            });
        }
    } else if (type.type === "boolean") {
        if (value) {
            value = formatMessage(messages.yes);
        } else {
            value = formatMessage(messages.no);
        }
    } else if (type.type === "date") {
        if (value !== null) {
            value = <FormattedDate key={idx} value={value}/>;
        }
    } else if (type.type === "dateTime" || type.type === "datetime") {
        if (value !== null) {
            value = (<FormattedDate
                        day='numeric'
                        hour="numeric"
                        key={idx}
                        minute="numeric"
                        month='numeric'
                        second="numeric"
                        value={value}
                        year='numeric'
                    />);
        }
    } else if (type.type === "time") {
        if (value !== null) {
            value = <FormattedTime key={idx} value={value}/>;
        }
    } else if (type.type === "int") {
        if (value !== null) {
            value = <FormattedNumber key={idx} style="decimal" value={value}/>;
        }
    } else if (type.type === "currency") {
        value = value;
    } else if (type.type === "number") {
        if (value !== null) {
            value = <FormattedNumber key={idx} value={value}/>;
        }
    } else if (type.type === "expression") {
        if (value !== null) {
            value = expressionBuilder(value, new VisualBuilder());
        }
    } else if (type.type === "email") {
        if (value) {
            let idx2 = 0;
            let parsedEmails = emailAddresses.parseAddressList(value);
            let output = [];
            if (parsedEmails) {
                for (let a of parsedEmails) {
                    if (idx2 > 0) {
                        output.push(<span key={idx + "-" + idx2 + "-comma"}>, </span>);
                    }

                    if (a.address) {
                        output.push(
                            <a href={"mailto:" + a.address} key={idx + "-" + idx2}>{a.name ? (a.name + " <" + a.address + ">") : a.address}</a>
                        );
                    } else {
                        output.push(a.name);
                    }

                    idx2++;
                }
            } else {
                output.push(<a href={"mailto:" + value} key={idx + "-" + idx2}>{value}</a>);
            }
            value = output;
        }
    } else if (type.type === "URL") {
        if (value) {
            let linkValue = String(value);

            if (!value.startsWith("http:") &&
                !value.startsWith("https:") &&
                !value.startsWith("ftp:") &&
                !value.startsWith("mailto:")) {
                linkValue = "http://" + value;
            }

            value = <a href={linkValue} key={idx} target="_blank">{value}</a>;
        }
    } else if (type.type === "image") {
        if (value) {
            let linkValue = String(value);

            if (!value.startsWith("http:") &&
                !value.startsWith("https:") &&
                !value.startsWith("ftp:")) {
                linkValue = "http://" + value;
            }

            value = <img key={idx} src={linkValue} />;
        }
    } else if (type.type === "text") {
        if (isList) {
            value = maxTextLength(value);
        }
    } else if (type.type === "phoneNumber") {
        if (value) {
            let parsed = parse(value);
            if (parsed) {
                value = format(parsed, 'International');
            }
        }
    } else if (type.type === "html") {
        if (value) {
            value = <SafeHTML value={renderFunction(value)} />;
        }
    } else if (value === null || value === undefined) {
        value = "";
    } else {
        value = String(value);
    }

    return value;
}

export function maxTextLength(text, len) {
    if (!text) {
        return text;
    }

    if (!len) {
        len = 60;
    }
    if (text.length > len) {
        return text.substr(0, len) + "…";
    }
    return text;
}
